import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetStoreExpensesQuery,
  useDeleteStoreExpenseMutation,

} from '../../slices/storeExpensesApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const StoreExpenseListScreen = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetStoreExpensesQuery({ pageNumber });
  const [deleteStoreExpense, { isLoading: loadingDelete }] = useDeleteStoreExpenseMutation();
  
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
 
  // Delete an storeExpense
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeExpense?')) {
      try {
        await deleteStoreExpense(id).unwrap();
        toast.success('StoreExpense deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort storeExpenses
  const filterAndSortStoreExpenses = () => {
    let filteredStoreExpenses = [...(data?.storeExpenses || [])];

    // Filter by text input
    if (filter) {
      filteredStoreExpenses = filteredStoreExpenses.filter(
        (storeExpense) =>
          storeExpense.account?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.details?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.category?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          storeExpense.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredStoreExpenses = filteredStoreExpenses.filter((storeExpense) => {
        const storeExpenseDate = new Date(storeExpense.date);
        return storeExpenseDate >= start && storeExpenseDate <= end;
      });
    }

    // Sort storeExpenses
    if (sortField) {
      filteredStoreExpenses.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredStoreExpenses;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredStoreExpenses = data?.storeExpenses?.filter((storeExpense) => {
      const storeExpenseDate = new Date(storeExpense.date);
      return storeExpenseDate >= start && storeExpenseDate <= end;
    }) || [];

    return filteredStoreExpenses.reduce((acc, storeExpense) => acc + storeExpense.amount, 0);
  };

  // Calculate total for all storeExpenses
  const totalAmount = data?.storeExpenses?.reduce((acc, storeExpense) => acc + storeExpense.amount, 0) || 0;

  const downloadStoreExpenseImages = async (storeExpense) => {
  if (!storeExpense.images || storeExpense.images.length === 0) {
    toast.warn("This storeExpense has no images");
    return;
  }

  const zip = new JSZip();
  const folder = zip.folder(`StoreExpense_${storeExpense._id}`);

  const imagePromises = storeExpense.images.map(async (imagePath) => {
    try {
      const normalized = imagePath.replace(/\\/g, "/");
      const response = await fetch(normalized);
      const blob = await response.blob();

      const filename = normalized.split("/").pop();
      folder.file(filename, blob);
    } catch (error) {
      console.error(`Failed to fetch ${imagePath}`, error);
    }
  });

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, `StoreExpense_${storeExpense._id}_Images.zip`);
    toast.success("Images downloaded successfully");
  });
};

const downloadAllImages = async () => {
  if (!startDate || !endDate) {
    toast.error("Please select a start and end date first");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const filteredStoreExpenses =
    data?.storeExpenses?.filter((storeExpense) => {
      const storeExpenseDate = new Date(storeExpense.date);
      return storeExpenseDate >= start && storeExpenseDate <= end;
    }) || [];

  // Count total images first
  const allImages = filteredStoreExpenses.flatMap((e) => e.images || []);

  if (allImages.length === 0) {
    toast.warn("No images found in the selected date range");
    return;
  }

  setIsDownloading(true);
  setDownloadProgress(0);

  const zip = new JSZip();
  const folder = zip.folder("All_StoreExpenses_Images");

  let processed = 0;

  const imagePromises = filteredStoreExpenses.flatMap((storeExpense) =>
    (storeExpense.images || []).map(async (imagePath) => {
      try {
        const normalized = imagePath.replace(/\\/g, "/");
        const response = await fetch(normalized);
        const blob = await response.blob();

        const filename = normalized.split("/").pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }

      processed += 1;
      setDownloadProgress(Math.round((processed / allImages.length) * 100));
    })
  );

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_StoreExpenses_Images.zip");

    toast.success(
      `Download complete. ${allImages.length} image${allImages.length !== 1 ? "s" : ""} included.`
    );

    setIsDownloading(false);
    setDownloadProgress(0);
  });
};  
      const exportToExcel = () => {

        const storeExpensesData = data?.storeExpenses?.map((storeExpense) => ({
          StoreExpenseId: storeExpense._id,
          Date: new Date(storeExpense.date).toLocaleDateString(),
          Account: storeExpense.account,
          AccountName: storeExpense.accountName,
          SubAccount: storeExpense.SubAccount,
          Details: storeExpense.details,
          Amount: `${storeExpense.amount.toFixed(2)}`,
          Category: storeExpense.category,
          Notes: storeExpense.notes,
          Images: storeExpense.images[0],
        }));
      
        const worksheet = XLSX.utils.json_to_sheet(storeExpensesData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'StoreExpenses');
      
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
        saveAs(file, 'StoreExpenses.xlsx');
      };

      const filteredTotalAmount = filterAndSortStoreExpenses().reduce(
  (acc, storeExpense) => acc + storeExpense.amount,
  0
   );

   const exportFilteredToExcel = () => {
  const filtered = filterAndSortStoreExpenses();

  if (filtered.length === 0) {
    toast.warn("No filtered data to export");
    return;
  }

  const storeExpensesData = filtered.map((storeExpense) => ({
    StoreExpenseId: storeExpense._id,
    Date: new Date(storeExpense.date).toLocaleDateString(),
    Account: storeExpense.account,
    AccountName: storeExpense.accountName,
    SubAccount: storeExpense.subAccount, // FIXED
    Details: storeExpense.details,
    Amount: `${storeExpense.amount.toFixed(2)}`,
    Category: storeExpense.category,
    Notes: storeExpense.notes,
    Username: storeExpense.username || "",
    Images: storeExpense.images?.[0] || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(storeExpensesData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "FilteredStoreExpenses");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const file = new Blob([excelBuffer], { type: "application/octet-stream" });

  saveAs(file, "Filtered_StoreExpenses.xlsx");
};
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>MiniIncome and StoreExpense</h1>
        </Col>
        <Col className="text-end">

  <Button
    className="my-3 mx-2"
    onClick={downloadAllImages}
    disabled={isDownloading}
  >
    {isDownloading ? (
      <>
        <span className="spinner-border spinner-border-sm me-2"></span>
        Zipping...
      </>
    ) : (
      <>
        <FaDownload /> Dld All Images
      </>
    )}
  </Button>

  {isDownloading && (
    <div className="my-2" style={{ width: "250px" }}>
      <div className="progress" style={{ height: "8px" }}>
        <div
          className="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          style={{ width: `${downloadProgress}%` }}
        ></div>
      </div>
      <small>{downloadProgress}%</small>
    </div>
  )}

          <Button className="my-3 mx-2" onClick={exportToExcel}>
          <FaDownload /> Exp to Exl
          </Button>
          <Button className="my-3 mx-2" onClick={exportFilteredToExcel}>
          <FaDownload /> Exp Filt
          </Button>
           
          <Link to='/create/storeExpense' className='btn btn-light my-3'>
            Add Data
          </Link>
          <Link to='/update/storeExpense/excelform' className='btn btn-light my-3'>
          ExcelUpdate
          </Link>
          <Link to='/bulk/update/storeExpense' className='btn btn-light my-3'>
          BulkUpdate
          </Link>
          <Link to='/admin/storeExpensesearchreplace' className='btn btn-light my-3'>
          Search
          </Link>

        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: {totalAmount.toFixed(2)}</h5>
        </Col>
        <Col>
    <h4>
      Total (Filtered): ${filteredTotalAmount.toFixed(2)}{' '}
      <span className="text-muted">({data?.storeExpenses?.length} total)</span>
    </h4>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.storeExpenses?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                User {sortField === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                Customer {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  ReceiptN {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
                <th>Dld</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortStoreExpenses().map((storeExpense) => (
                <tr key={storeExpense._id}>
                  <td>{new Date(storeExpense.date).toLocaleDateString()}</td>
                  <td>{storeExpense.username}</td>
                  <td>{storeExpense.accountName}</td>
                  <td>{storeExpense.details}</td>
                  <td>
                    {storeExpense.amount >= 0
                      ? `${storeExpense.amount.toFixed(2)}`
                      : `-${Math.abs(storeExpense.amount).toFixed(2)}`}
                  </td>
                  <td>{storeExpense.category}</td>
                  <td>{storeExpense.notes}</td>
                  
                  <td className="thumbnails">
                    {storeExpense.images?.length > 0 && (
                      <img
                        src={storeExpense.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2" 
                        style={{
                          width: '10px',
                          height: '10px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    
                    <Button
                      as={Link}
                      to={`/storeAdmin/storeExpense/${storeExpense._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(storeExpense._id)}
                    >
                      <FaTrash />
                    </Button>
                    
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/storeExpense/${storeExpense._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    
                  </td>
                  <td> 
                   <Button variant="info" className="btn-sm" onClick={() => downloadStoreExpenseImages(storeExpense)}>
                      <FaDownload /> Download Images
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="storeExpenselist" />
        </>
      ) : (
        <Message variant="info">No storeExpenses found</Message>
      )}
    </>
  );
};

export default StoreExpenseListScreen;
